import React, { useEffect, useState } from 'react';
import { CheckCircle, Award } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { getImageStyle } from '../components/ImagePositionEditor';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const About = () => {
  const [aboutContent, setAboutContent] = useState(null);
  const [siteInfo, setSiteInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [aboutRes, siteInfoRes] = await Promise.all([
        axios.get(`${API_URL}/about-lindsey`),
        axios.get(`${API_URL}/site-info`)
      ]);

      setAboutContent(aboutRes.data);
      setSiteInfo(siteInfoRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      // Fallback default
      setAboutContent({
        bio: "I'm Lindsey Mayr, a women's health coach specializing in Breast Implant Illness and explant surgery support.",
        image: "https://customer-assets.emergentagent.com/job_0611684f-3585-4c13-8ded-0605552a5675/artifacts/88szxmlf_IMGL4002.jpg",
        certifications: ["Certified Life Coach", "Breast Implant Illness Specialist", "Published Author", "Wellness Advocate"],
        mission: "My mission is to empower women with knowledge, support, and hope as they navigate breast implant illness and the explant journey."
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-sage-50 via-lavender-50 to-cream-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-6">
            <h1 className="text-5xl md:text-6xl font-serif text-gray-900">About Lindsey</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A passionate advocate for women's health and wellness
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1 space-y-6">
              <h2 className="text-4xl font-serif text-gray-900">My Story</h2>
              <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
                <p>{aboutContent?.bio}</p>
                <p className="text-sage-800 font-medium italic">"{aboutContent?.mission}"</p>
              </div>

              {/* Certifications */}
              <div className="space-y-4 pt-8">
                <h3 className="text-2xl font-semibold text-gray-900 flex items-center">
                  <Award className="h-6 w-6 mr-2 text-sage-700" />
                  Qualifications
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {aboutContent?.certifications?.map((cert, index) => (
                    <div key={index} className="flex items-center space-x-3 bg-sage-50 p-4 rounded-lg">
                      <CheckCircle className="h-5 w-5 text-sage-700 flex-shrink-0" />
                      <span className="text-gray-800">{cert}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <div className="relative">
                <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src={aboutContent?.image || "https://customer-assets.emergentagent.com/job_0611684f-3585-4c13-8ded-0605552a5675/artifacts/88szxmlf_IMGL4002.jpg"}
                    alt="Lindsey Mayr"
                    className="w-full h-full"
                    style={getImageStyle(aboutContent?.imagePosition)}
                  />
                </div>
                <div className="absolute -bottom-8 -left-8 bg-white p-6 rounded-2xl shadow-xl">
                  <p className="text-sm text-gray-600 mb-1">Lindsey Mayr</p>
                  <p className="text-lg font-semibold text-gray-900">Women's Health Coach</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-sage-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-0 shadow-xl bg-white">
            <CardContent className="p-12">
              <div className="text-center space-y-6">
                <h2 className="text-4xl font-serif text-gray-900">My Mission</h2>
                <p className="text-xl text-gray-700 leading-relaxed">
                  To empower women with knowledge, support, and hope as they navigate breast implant illness and the explant journey. Every woman deserves to feel heard, supported, and confident in her healing journey.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* YouTube Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          <h2 className="text-4xl font-serif text-gray-900">Visit My YouTube Channel</h2>
          <p className="text-lg text-gray-600">
            Subscribe for weekly videos on healing, wellness, and navigating the explant journey
          </p>
          <a
            href={siteInfo?.social_media?.youtube || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-8 py-4 bg-sage-700 text-white rounded-xl hover:bg-sage-800 transition-all duration-300 hover:shadow-lg font-medium text-lg"
          >
            Watch on YouTube
          </a>
        </div>
      </section>
    </div>
  );
};

export default About;